/**
 * Один переключатель для трёх объёмных видов (запрос владельца 28.07): раньше
 * они были разбросаны — «Объём» в шапке, «Объём · N эт.» и «Объём 3D» внутри
 * карточки здания, и человек не понимал, что это варианты одного и того же.
 *
 * Устроен как выбор с памятью: слева — стрелка со списком, справа от неё
 * последний выбранный вариант. Он запоминается и в следующий раз открывается
 * первым, поэтому привычный вид — в одно нажатие.
 */
import { tr } from '@app/i18n'
import { buildingFloors, containerUnitIds, type Venue } from '@app/rvf'
import { useEffect, useRef, useState } from 'preact/hooks'
import {
  anchorId,
  floorStackFloor,
  floorStackUnitId,
  langTick,
  targetId,
  venue,
  viewMode,
  volume3dUnitId,
} from '../state'
import { volumeTier } from '../volume3d/capability'
import { prefetchVolume3d } from '../volume3d/prefetch'

export type VolumeKind = 'iso' | 'stack' | 'three'

const STORE_KEY = 'volume-kind'

/** Последний выбор человека переживает перезагрузку — это его привычка. */
function readStored(): VolumeKind | null {
  try {
    const raw = localStorage.getItem(STORE_KEY)
    return raw === 'iso' || raw === 'stack' || raw === 'three' ? raw : null
  } catch {
    return null
  }
}

function store(kind: VolumeKind): void {
  try {
    localStorage.setItem(STORE_KEY, kind)
  } catch {
    // приватный режим — просто не запомним
  }
}

/** Главное здание площадки: контейнер с этажами (в торговом центре он один). */
export function mainBuildingOf(v: Venue): string | null {
  const containers = containerUnitIds(v)
  let best: string | null = null
  let bestFloors = 1
  for (const unit of v.units) {
    if (!containers.has(unit.id)) continue
    const floors = buildingFloors(v, unit)
    if (floors > bestFloors) {
      bestFloors = floors
      best = unit.id
    }
  }
  return best
}

interface Option {
  readonly kind: VolumeKind
  readonly label: string
  readonly hint: string
  readonly open: () => void
  readonly prefetch?: () => void
}

/**
 * Адрес отдельной страницы «вся площадка, этажи рядом» (`/mall/{venue}`).
 * Цель и якорь переносим теми же именами, что в печатном QR-контракте — человек
 * переходит на другой экран, но не теряет, куда он шёл.
 */
export function mallHref(venueIdValue: string): string {
  const params = new URLSearchParams()
  if (targetId.value) params.set('to', targetId.value)
  if (anchorId.value) params.set('a', anchorId.value)
  const query = params.toString()
  return `/mall/${encodeURIComponent(venueIdValue)}${query ? `?${query}` : ''}`
}

function optionsFor(v: Venue | null, buildingId: string | null): Option[] {
  const out: Option[] = []
  if ((v?.levels.length ?? 0) > 1) {
    out.push({
      kind: 'iso',
      label: tr('volume.kind_iso'),
      hint: tr('volume.kind_iso_hint'),
      // Отдельная СТРАНИЦА, а не режим вьюера: у неё свой бандл и свои экраны
      // (справочник, карточка места, маршруты), а вьюер остаётся лёгким —
      // «карта за две секунды» (решение владельца 29.07).
      open: () => {
        const id = v?.venue.id
        if (id) location.assign(mallHref(id))
      },
    })
  }
  if (buildingId) {
    out.push({
      kind: 'stack',
      label: tr('volume.kind_stack'),
      hint: tr('volume.kind_stack_hint'),
      open: () => {
        floorStackFloor.value = 1
        floorStackUnitId.value = buildingId
      },
    })
    if (v?.venue.volume3d !== false && volumeTier() !== 'off') {
      out.push({
        kind: 'three',
        label: tr('volume.kind_three'),
        hint: tr('volume.kind_three_hint'),
        prefetch: prefetchVolume3d,
        open: () => {
          volume3dUnitId.value = buildingId
        },
      })
    }
  }
  return out
}

export function VolumeSwitch() {
  // Подписка на смену языка: без неё подписи вариантов оставались на прежнем
  // языке, пока компонент не перерисуется по другой причине.
  langTick.value
  const v = venue.value
  const [open, setOpen] = useState(false)
  const [kind, setKind] = useState<VolumeKind | null>(readStored)
  const boxRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent): void => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    window.addEventListener('pointerdown', onDown)
    return () => window.removeEventListener('pointerdown', onDown)
  }, [open])

  const buildingId = v ? mainBuildingOf(v) : null
  const options = optionsFor(v, buildingId)
  if (options.length === 0) return null

  // Показываем запомненный вариант; если он для этой площадки недоступен — первый.
  const current = options.find((o) => o.kind === kind) ?? options[0]
  if (!current) return null
  const inIso = viewMode.value === 'iso'

  const choose = (option: Option): void => {
    setKind(option.kind)
    store(option.kind)
    setOpen(false)
    option.open()
  }

  return (
    <div ref={boxRef} class="vol-wrap">
      {inIso ? (
        <button
          type="button"
          class="lang-btn lang-btn-on"
          onClick={() => {
            viewMode.value = 'map'
          }}
        >
          {tr('view.map')}
        </button>
      ) : (
        <div class="vol-split">
          <button
            type="button"
            class="vol-arrow"
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label={tr('volume.choose')}
            onClick={() => setOpen((x) => !x)}
          >
            <span aria-hidden="true">▾</span>
          </button>
          <button
            type="button"
            class="vol-main"
            onPointerEnter={current.prefetch}
            onFocus={current.prefetch}
            onClick={() => choose(current)}
          >
            {current.label}
          </button>
        </div>
      )}

      {open && !inIso && (
        <div class="vol-menu glass" role="listbox">
          {options.map((option) => (
            <button
              key={option.kind}
              type="button"
              class={`vol-item${option.kind === current.kind ? ' vol-item-on' : ''}`}
              role="option"
              aria-selected={option.kind === current.kind}
              onPointerEnter={option.prefetch}
              onClick={() => choose(option)}
            >
              <span class="vol-item-name">{option.label}</span>
              <span class="vol-item-hint">{option.hint}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
